import React from "react";
import {
  LayoutDashboard,
  Users,
  Building2,
  UserCircle2,
  FileSignature,
  Stethoscope,
  IdCard,
  ClipboardList,
  CalendarDays,
  Ambulance,
  Files,
  FolderLock,
  BarChart3,
  HelpCircle,
  MessageSquare,
  Settings as SettingsIcon,
  Undo2,
} from "lucide-react";

import { Logo } from "@/components/Logo";

export const NAV_GROUPS = [
  {
    label: "Overview",
    items: [
      { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, badge: null },
    ],
  },
  {
    label: "Pipeline",
    items: [
      { id: "consumer", label: "Leads", icon: Users, badge: null },
      { id: "facility", label: "Facilities & Providers", icon: Building2, badge: null },
      { id: "clients", label: "Clients", icon: UserCircle2, badge: null },
      { id: "contracts", label: "Contracts", icon: FileSignature, badge: null },
      { id: "refunds", label: "Refund Center", icon: Undo2, badge: null },
    ],
  },
  {
    label: "Operations",
    items: [
      { id: "services", label: "Services", icon: Stethoscope, badge: null },
      { id: "staff", label: "Employees", icon: IdCard, badge: null },
      { id: "tasks", label: "Tasks & Notes", icon: ClipboardList, badge: null },
      { id: "calendar", label: "Calendar", icon: CalendarDays, badge: null },
      { id: "dispatch", label: "Dispatch Center", icon: Ambulance, badge: null },
    ],
  },
  {
    label: "Records",
    items: [
      { id: "operations", label: "Documents", icon: FolderLock, badge: null },
      { id: "documents", label: "Public Vault", icon: Files, badge: null },
      { id: "reports", label: "Reports & Analytics", icon: BarChart3, badge: null },
    ],
  },
  {
    label: "Support",
    items: [
      { id: "help", label: "Help Center", icon: HelpCircle, badge: null },
      { id: "chat", label: "Team Chat", icon: MessageSquare, badge: null, dynamicBadge: "chatUnread" },
      { id: "settings", label: "Settings", icon: SettingsIcon, badge: null, ownerOnly: true },
    ],
  },
];

export default function AdminSidebar({ active, onSelect, user, collapsed = false, dynamicBadges = {} }) {
  return (
    <aside
      className="flex flex-col border-r"
      style={{
        background: "#0B2545",
        borderColor: "rgba(255,255,255,0.06)",
        width: collapsed ? 76 : 264,
        transition: "width 200ms ease",
      }}
      data-testid="admin-sidebar"
    >
      <div className="px-5 py-5 flex items-center gap-3 border-b" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
        <Logo size="sm" inverted showText={!collapsed} />
      </div>

      <div className="flex-1 overflow-y-auto py-4">
        {NAV_GROUPS.map((group) => {
          const items = group.items.filter((item) => !item.ownerOnly || user?.id === "beckett");
          if (items.length === 0) return null;
          return (
          <div key={group.label} className="px-3 mb-4">
            {!collapsed && (
              <div
                className="px-3 mb-1.5 text-[10px] uppercase tracking-[0.22em] font-medium"
                style={{ color: "rgba(212,175,55,0.85)" }}
              >
                {group.label}
              </div>
            )}
            <nav className="flex flex-col gap-0.5">
              {items.map((item) => {
                const Icon = item.icon;
                const isActive = active === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => onSelect(item.id)}
                    data-testid={`nav-${item.id}`}
                    className="group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition-colors"
                    style={{
                      background: isActive ? "rgba(212,175,55,0.18)" : "transparent",
                      color: isActive ? "#FFD774" : "rgba(241,245,249,0.85)",
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive) e.currentTarget.style.background = "rgba(255,255,255,0.04)";
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) e.currentTarget.style.background = "transparent";
                    }}
                  >
                    {isActive && (
                      <span
                        className="absolute left-0 top-2 bottom-2 w-1 rounded-r"
                        style={{ background: "#D4AF37" }}
                      />
                    )}
                    <Icon className="h-4 w-4 shrink-0" />
                    {!collapsed && (
                      <>
                        <span className="flex-1 truncate font-medium">{item.label}</span>
                        {item.dynamicBadge && dynamicBadges[item.dynamicBadge] > 0 && (
                          <span
                            className="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                            style={{ background: "#9B1C1C", color: "#FFFFFF", minWidth: 18, textAlign: "center" }}
                            data-testid={`sidebar-badge-${item.id}`}
                          >
                            {dynamicBadges[item.dynamicBadge] > 99 ? "99+" : dynamicBadges[item.dynamicBadge]}
                          </span>
                        )}
                        {item.badge && (
                          <span
                            className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded"
                            style={{ background: "rgba(255,255,255,0.08)", color: "rgba(241,245,249,0.6)" }}
                          >
                            {item.badge}
                          </span>
                        )}
                      </>
                    )}
                  </button>
                );
              })}
            </nav>
          </div>
          );
        })}
      </div>

      {user && !collapsed && (
        <div
          className="px-4 py-4 border-t flex items-center gap-3"
          style={{ borderColor: "rgba(255,255,255,0.08)" }}
          data-testid="sidebar-user-card"
        >
          <span
            className="grid place-items-center h-9 w-9 rounded-full font-display text-sm font-bold shrink-0"
            style={{ background: "rgba(212,175,55,0.22)", color: "#FFD774" }}
          >
            {user.name[0]}
          </span>
          <div className="leading-tight min-w-0">
            <div className="text-sm font-semibold truncate" style={{ color: "#F1F5F9" }}>{user.name}</div>
            <div className="text-[10px] uppercase tracking-[0.18em] truncate" style={{ color: "rgba(212,175,55,0.85)" }}>
              {user.role}
            </div>
          </div>
        </div>
      )}
    </aside>
  );
}
